import { type FC } from "react";
import { Info } from "lucide-react";

import { Label } from "@/shared/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipInfoTrigger,
  TooltipProvider,
} from "@/shared/components/ui/tooltip";
import { cn } from "@/shared/utils/core";

import type { FormField, FormFieldTooltip } from "@/shared/types/estateEssentials";

import {
  getGridClass,
  groupFieldsByRow,
  resolveFieldLabel,
  resolveTemplate,
} from "../utils/formDisplayUtils";
import { FormFieldInput } from "./FormFieldInput";

export function FieldTooltip({ tooltip }: { tooltip: FormFieldTooltip }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipInfoTrigger aria-label={tooltip.title} />
        <TooltipContent title={tooltip.title}>{tooltip.content}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function FieldLabel({ field, className }: { field: FormField; className?: string }) {
  return (
    <Label htmlFor={field.key} className={cn("flex items-center gap-1", className)}>
      {field.label}
      {field.tooltip && <FieldTooltip tooltip={field.tooltip} />}
    </Label>
  );
}

interface FieldGridProps {
  fields: FormField[];
  values: Record<string, string>;
  errors: Record<string, string>;
  onFieldChange: (key: string, value: string) => void;
  relationship?: string;
  attentionKeys?: Set<string>;
  deceasedFirstName?: string;
  portalContainer?: HTMLElement | null;
}

export const FieldGrid: FC<FieldGridProps> = ({
  fields,
  values,
  errors,
  onFieldChange,
  relationship,
  attentionKeys,
  deceasedFirstName,
  portalContainer,
}) => {
  const visibleFields = fields.filter((field) => {
    const isMarriageField = field.key === "marriage_date" || field.key === "marriage_place";
    if (isMarriageField) {
      return relationship === "spouse" || relationship === "former-spouse";
    }
    return true;
  });

  const groups = groupFieldsByRow(visibleFields);

  return (
    <div className="space-y-3">
      {groups.map((group) => (
        <div key={group[0].key} className={getGridClass(group.length)}>
          {group.map((field) => {
            const label = resolveFieldLabel(field, deceasedFirstName);

            const callout = field.infoCalloutByValue?.[values[field.key]] ?? field.infoCallout;

            return (
              <div key={field.key} className="space-y-1">
                <FieldLabel field={{ ...field, label }} className="text-sm" />
                <FormFieldInput
                  field={{ ...field, label }}
                  value={values[field.key] ?? ""}
                  onFieldChange={onFieldChange}
                  fieldKey={field.key}
                  error={!!errors[field.key]}
                  attention={!!attentionKeys?.has(field.key) && !values[field.key]?.trim()}
                  portalContainer={portalContainer}
                />
                {errors[field.key] && errors[field.key] !== "Required" && (
                  <p className="text-2xs text-amber-600">{errors[field.key]}</p>
                )}
                {field.helperText && (
                  <p className="text-2xs text-secondary">
                    {resolveTemplate(field.helperText, deceasedFirstName)}
                  </p>
                )}
                {callout && (
                  <div className="!mt-3 flex gap-2.5 rounded-lg border border-primary bg-subtle px-4 py-3">
                    <Info className="size-4 shrink-0 text-tertiary mt-0.5" />
                    <div>
                      <p className="text-sm font-medium">
                        {resolveTemplate(callout.title, deceasedFirstName)}
                      </p>
                      <p className="text-sm text-secondary mt-1">
                        {resolveTemplate(callout.content, deceasedFirstName)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};
